import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const meetingsConfigurationApi = {
  // ============================================
  // Meeting Types CRUD
  // ============================================

  getAllMeetingTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/types`);
    } catch (error) {
      throw new ApiError('Failed to fetch meeting types', error.status || 500);
    }
  },

  getMeetingTypeById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting type not found', 404);
      }
      throw new ApiError('Failed to fetch meeting type', error.status || 500);
    }
  },

  createMeetingType: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/types`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting type data', 400);
      }
      throw new ApiError('Failed to create meeting type', error.status || 500);
    }
  },

  updateMeetingType: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/types/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting type data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Meeting type not found', 404);
      }
      throw new ApiError('Failed to update meeting type', error.status || 500);
    }
  },

  deleteMeetingType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting type not found', 404);
      }
      throw new ApiError('Failed to delete meeting type', error.status || 500);
    }
  },

  // ============================================
  // Attendee Types CRUD
  // ============================================

  getAllAttendeeTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/attendee-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch attendee types', error.status || 500);
    }
  },

  getAttendeeTypeById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/attendee-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Attendee type not found', 404);
      }
      throw new ApiError('Failed to fetch attendee type', error.status || 500);
    }
  },

  createAttendeeType: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/attendee-types`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid attendee type data', 400);
      }
      throw new ApiError('Failed to create attendee type', error.status || 500);
    }
  },

  updateAttendeeType: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/attendee-types/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid attendee type data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Attendee type not found', 404);
      }
      throw new ApiError('Failed to update attendee type', error.status || 500);
    }
  },

  deleteAttendeeType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/attendee-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Attendee type not found', 404);
      }
      throw new ApiError('Failed to delete attendee type', error.status || 500);
    }
  },

  // ============================================
  // Meeting Statuses CRUD
  // ============================================

  getAllMeetingStatuses: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/statuses`);
    } catch (error) {
      throw new ApiError('Failed to fetch meeting statuses', error.status || 500);
    }
  },

  getMeetingStatusById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/statuses/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting status not found', 404);
      }
      throw new ApiError('Failed to fetch meeting status', error.status || 500);
    }
  },

  createMeetingStatus: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/statuses`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting status data', 400);
      }
      throw new ApiError('Failed to create meeting status', error.status || 500);
    }
  },

  updateMeetingStatus: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/statuses/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting status data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Meeting status not found', 404);
      }
      throw new ApiError('Failed to update meeting status', error.status || 500);
    }
  },

  deleteMeetingStatus: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/statuses/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting status not found', 404);
      }
      throw new ApiError('Failed to delete meeting status', error.status || 500);
    }
  },

  // ============================================
  // Meeting Relation Types CRUD
  // ============================================

  getAllMeetingRelationTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/relation-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch meeting relation types', error.status || 500);
    }
  },

  getMeetingRelationTypeById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/relation-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting relation type not found', 404);
      }
      throw new ApiError('Failed to fetch meeting relation type', error.status || 500);
    }
  },

  createMeetingRelationType: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/relation-types`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting relation type data', 400);
      }
      throw new ApiError('Failed to create meeting relation type', error.status || 500);
    }
  },

  updateMeetingRelationType: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/relation-types/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting relation type data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Meeting relation type not found', 404);
      }
      throw new ApiError('Failed to update meeting relation type', error.status || 500);
    }
  },

  deleteMeetingRelationType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/relation-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting relation type not found', 404);
      }
      throw new ApiError('Failed to delete meeting relation type', error.status || 500);
    }
  },

  // ============================================
  // System Talking Points CRUD
  // ============================================

  getAllTalkingPoints: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/talking-points/all`);
    } catch (error) {
      throw new ApiError('Failed to fetch talking points', error.status || 500);
    }
  },

  getPublishedTalkingPoints: async (meetingTypeIds = null) => {
    try {
      let url = `${API_BASE_URL}/meetings/talking-points`;
      if (meetingTypeIds && meetingTypeIds.length > 0) {
        url += `?meetingTypeIds=${meetingTypeIds.join(',')}`;
      }
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch talking points', error.status || 500);
    }
  },

  getTalkingPointById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/talking-points/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Talking point not found', 404);
      }
      throw new ApiError('Failed to fetch talking point', error.status || 500);
    }
  },

  createTalkingPoint: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/talking-points`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid talking point data', 400);
      }
      throw new ApiError('Failed to create talking point', error.status || 500);
    }
  },

  updateTalkingPoint: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/talking-points/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid talking point data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Talking point not found', 404);
      }
      throw new ApiError('Failed to update talking point', error.status || 500);
    }
  },

  deleteTalkingPoint: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/talking-points/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Talking point not found', 404);
      }
      throw new ApiError('Failed to delete talking point', error.status || 500);
    }
  },
};
